import {
  CenterTypes, GrantStatus, DefaultLocale, Ids, SK,
} from '@constants';
import { SupportedLocales, loadLocale } from '@constants/locales';
import { readCachedFontForCurrentCenter } from '@constants/fonts';
// Direct file import: the '@utils' barrel re-exports translation.js, which imports this store.
import { readEupReference, writeEupReference } from '@utils/eup-reference';
import { ChannelMode, isCpEditable } from '@components/communication-channels';
import { create } from 'zustand';

// Hydrate the customFont from localStorage before the store is created, from
// whichever center the URL points at (consent-center is cached per consent
// profile, privacy-center per principal). On returning visits this makes the
// @font-face rule and the FontFace download start at mount, in parallel with
// the config API, instead of only once the modal opens — that's what removes
// the visible Lato→CustomFont swap. The API response overwrites it a moment
// later, so a tenant that swapped fonts self-corrects on this same open.
const initialCustomFont = readCachedFontForCurrentCenter();

// The remembered locale lives in sessionStorage.eupReference and is an
// EUP-only convenience: the helpers ignore public flows (consent / privacy /
// cookie centers, assessment forms), so those open in the locale their link or
// config dictates and a switch made there never leaks into the dashboard.
const readPersistedLocale = () => {
  const { locale } = readEupReference();
  return SupportedLocales.includes(locale) ? locale : DefaultLocale;
};

const writePersistedLocale = (locale) => writeEupReference({ locale });

// Sequence counter used to discard out-of-order locale switch resolutions:
// if the user selects A then quickly selects B, and B resolves before A,
// the late A resolution must not overwrite B in the store or localStorage.
let localeRequestSeq = 0;

const initialState = {
  isWebSdk: false,
  isAndroidSdk: false,
  isIosSdk: false,
  nonce: '',
  locale: readPersistedLocale(),
  isLocaleLoading: false,
  shouldAuthenticateFirst: false,
  isOtpVerified: false,
  isEmailAuthentication: false,
  principalId: '',
  principalEmail: '',
  principalPhone: '',
  signedPublicCaptureUrl: '',
  errors: [],
  bannerUrl: '',
  customFont: initialCustomFont,
  purposes: [],
  showAcceptAllButton: true,
  cardViewLayout: false,
  isOneTimeNotice: false,
  isOneTimeNoticeAccepted: false,
  isOtpSent: false,
  otpRequestUrl: '',
  otpPayload: '',
  otpTransactionId: '',
  otpResendUrl: '',
  otpVerifyUrl: '',
  otpConfigUrl: '',
  trackingId: null,
  privacyCenterActiveTabId: Ids.REQUEST_CENTER,
  consentCenterUrl: '',
  requestCenterUrl: '',
  preferenceCenterUrl: '',
  privacyCenterUrl: '',
  requestCollectUrl: '',
  preferenceUpdateUrl: '',
  consentProfilesPurposes: [],
  response: {},
  centerType: '',
  selectedRequestTypeId: null,
  requestFormData: {},
  isRequestFormValidationError: false,
  clientMetaData: null,
  verifiedLocales: [],
  redirectUrl: '',
  showChannelsOnTop: false,
  showChannelsOnBottom: false,
  showChannelsPurposeWise: false,
};

const useStore = create((set) => ({
  ...initialState,
  actions: {
    setLocaleAction: async (locale) => {
      if (!SupportedLocales.includes(locale)) return;
      // Locale chunks are lazy-loaded. Wait for the labels to be in cache
      // before flipping the store, otherwise consumers subscribed via
      // useLocale re-render against an empty cache and fall back to English.
      localeRequestSeq += 1;
      const seq = localeRequestSeq;
      set(() => ({ isLocaleLoading: true }));
      await loadLocale(locale);
      // Discard if a newer selection has superseded this one — otherwise the
      // slower earlier request could overwrite the latest choice on resolve.
      if (seq !== localeRequestSeq) return;
      writePersistedLocale(locale);
      set(() => ({ locale, isLocaleLoading: false }));
    },
    unSelectAllPurposes: () => set((state) => ({
      purposes: state.purposes.map((purpose) => ({
        ...purpose,
        selected: false,
      })),
    })),
    selectAllPurposes: () => set((state) => ({ purposes: state.purposes.map((purpose) => ({ ...purpose, selected: true })) })),
    togglePurposeSelectionById: (id) => {
      set((state) => ({
        purposes: state.purposes.map((purpose) => {
          if (purpose.id === id) {
            return {
              ...purpose,
              selected: !purpose.selected,
            };
          }
          return purpose;
        }),
      }));
    },
    // Bulk-toggle a list of purpose ids to a target value. Used by the group
    // header to flip every purpose inside the group at once.
    setPurposesSelected: (ids, value) => {
      set((state) => ({
        purposes: state.purposes.map((purpose) => {
          if (!ids.includes(purpose.id)) return purpose;
          return { ...purpose, selected: value };
        }),
      }));
    },
    toggleIsEmailAuthentication: () => set((state) => {
      return ({ isEmailAuthentication: !state.isEmailAuthentication });
    }),
    resetOtp: () => set(() => {
      return ({
        isOtpSent: false, otpPayload: '', otpTransactionId: '',
      });
    }),
    resetOtpVerification: () => set({ isOtpVerified: false, shouldAuthenticateFirst: true }),
    setConfig: (config) => set(() => ({ ...config })),
    setPrivacyCenterActiveTabId: (id) => set(() => ({ privacyCenterActiveTabId: id })),
    setIsOneTimeNoticeAccepted: () => set((state) => ({ isOneTimeNoticeAccepted: !state.isOneTimeNoticeAccepted })),
    toggleConsentProfilesPurposesSelection: (id, purposeId) => set((state) => ({
      consentProfilesPurposes: state.consentProfilesPurposes.map((profile) => {
        if (profile.id === id) {
          return {
            ...profile,
            purposes: profile.purposes.map((purpose) => {
              if (purpose.purposeId === purposeId) {
                return {
                  ...purpose,
                  selected: !purpose.selected,
                };
              }
              return purpose;
            }),
          };
        }
        return profile;
      }),
    })),
    toggleConsentProfilesPurposeChannel: (id, purposeId, channelId) => set((state) => ({
      consentProfilesPurposes: state.consentProfilesPurposes.map((profile) => {
        if (profile.id !== id) return profile;
        return {
          ...profile,
          purposes: profile.purposes.map((purpose) => {
            if (purpose.purposeId !== purposeId) return purpose;
            const cp = (purpose.communicationPreferences || []).find((c) => c.channel === channelId);
            if (!isCpEditable(cp, ChannelMode.EUP)) return purpose;
            const current = purpose.channelState?.[channelId] || GrantStatus.REVOKED;
            const next = current === GrantStatus.GRANTED ? GrantStatus.REVOKED : GrantStatus.GRANTED;
            return {
              ...purpose,
              channelState: { ...(purpose.channelState || {}), [channelId]: next },
            };
          }),
        };
      }),
    })),
    setConsentProfilesPurposesChannel: (id, channelId, nextState) => set((state) => ({
      consentProfilesPurposes: state.consentProfilesPurposes.map((profile) => {
        if (profile.id !== id) return profile;
        return {
          ...profile,
          purposes: profile.purposes.map((purpose) => {
            const cp = (purpose.communicationPreferences || []).find((c) => c.channel === channelId);
            if (!isCpEditable(cp, ChannelMode.EUP)) return purpose;
            return {
              ...purpose,
              channelState: { ...(purpose.channelState || {}), [channelId]: nextState },
            };
          }),
        };
      }),
    })),
    toggleConsentPurposeChannel: (purposeId, channelId) => set((state) => ({
      purposes: state.purposes.map((purpose) => {
        if (purpose.id !== purposeId) return purpose;
        const currentState = purpose.channelState?.[channelId] || GrantStatus.REVOKED;
        const cp = (purpose.communicationPreferences || []).find((c) => c.channel === channelId);
        if (!isCpEditable(cp, ChannelMode.NOTICE)) return purpose;
        const next = currentState === GrantStatus.GRANTED ? GrantStatus.REVOKED : GrantStatus.GRANTED;
        return {
          ...purpose,
          channelState: { ...(purpose.channelState || {}), [channelId]: next },
        };
      }),
    })),
    setConsentPurposesChannel: (channelId, nextState) => set((state) => ({
      purposes: state.purposes.map((purpose) => {
        const cp = (purpose.communicationPreferences || []).find((c) => c.channel === channelId);
        if (!isCpEditable(cp, ChannelMode.NOTICE)) return purpose;
        return {
          ...purpose,
          channelState: { ...(purpose.channelState || {}), [channelId]: nextState },
        };
      }),
    })),
    resetTrackingId: () => set(() => ({ trackingId: null })),
    setSelectedRequestTypeId: (selectedRequestTypeId) => set(() => ({ selectedRequestTypeId })),
    setRequestFormData: (requestFormData) => set(() => ({ requestFormData })),
    handleRequestFormDataChange: (id, value) => set((state) => {
      return ({
        requestFormData: {
          ...state.requestFormData,
          [id]: {
            ...state.requestFormData?.[id],
            value,
          },
        },
      });
    }),
    setPreferenceCenterUrl: (url) => set(() => ({ preferenceCenterUrl: url })),
    setRequestFormValidationError: (isRequestFormValidationError) => set(() => ({ isRequestFormValidationError })),
    resetEup: () => set((state) => ({
      ...initialState,
      otpConfigUrl: state.otpConfigUrl,
      requestCollectUrl: state.requestCollectUrl,
    })),
    setClientMetaData: (clientMetaData) => set(() => ({ clientMetaData })),
  },

}));

export { useStore };
export const useStoreActions = () => useStore.getState().actions;

// Populated by fetchConsentProfile from consentUI.cssStyleProperties (WEIGHT_*).
// Referentially stable — safe for zustand subscribers.
export const useCustomFontConfig = () => useStore((state) => state.customFont);

export const useIsWebSdk = () => useStore((state) => state.isWebSdk);
export const useIsAndroidSdk = () => useStore((state) => state.isAndroidSdk);
export const useIsIosSdk = () => useStore((state) => state.isIosSdk);
export const useIsSdk = () => useStore((state) => state.isWebSdk || state.isAndroidSdk || state.isIosSdk);
export const useIsMobileSdk = () => useStore((state) => state.isAndroidSdk || state.isIosSdk);
export const useNonce = () => useStore((state) => state.nonce);
export const useLocale = () => useStore((state) => state.locale);
export const useIsLocaleLoading = () => useStore((state) => state.isLocaleLoading);
export const useShouldAuthenticateFirst = () => useStore((state) => state.shouldAuthenticateFirst);
export const useIsOtpVerified = () => useStore((state) => state.isOtpVerified);
export const useIsEmailAuthentication = () => useStore((state) => state.isEmailAuthentication);
export const useIsOtpSent = () => useStore((state) => state.isOtpSent);
export const usePrincipalId = () => useStore((state) => state.principalId);
export const usePrincipalEmail = () => useStore((state) => state.principalEmail);
export const usePrincipalPhone = () => useStore((state) => state.principalPhone);
export const useOtpToken = () => useStore((state) => state.otpToken);
export const useCaptureUrl = () => useStore((state) => state.signedPublicCaptureUrl);
export const useRequestCollectUrl = () => useStore((state) => state.requestCollectUrl);

export const useBannerUrl = () => useStore((state) => state.bannerUrl);

export const usePurposes = () => useStore((state) => state.purposes);
export const useShowAcceptAllButton = () => useStore((state) => state.showAcceptAllButton);
export const useCardViewLayout = () => useStore((state) => state.cardViewLayout);
export const useShowChannelsOnTop = () => useStore((state) => state.showChannelsOnTop);
export const useShowChannelsOnBottom = () => useStore((state) => state.showChannelsOnBottom);
export const useShowChannelsPurposeWise = () => useStore((state) => state.showChannelsPurposeWise);
export const useIsOneTimeNotice = () => useStore((state) => state.isOneTimeNotice);
export const useIsOneTimeNoticeAccepted = () => useStore((state) => state.isOneTimeNoticeAccepted);

export const useTrackingId = () => useStore((state) => state.trackingId);

export const useErrors = () => useStore((state) => state.errors);

export const usePrivacyCenterActiveTabId = () => useStore((state) => state.privacyCenterActiveTabId);

export const useCollectUrl = () => useStore((state) => state.consentCenterUrl);

export const useRequestUrl = () => useStore((state) => {
  if (
    state.centerType === CenterTypes.PRIVACY_CENTER
      && state.privacyCenterActiveTabId === Ids.REQUEST_CENTER
  ) {
    return state.privacyCenterUrl;
  }
  return state.requestCenterUrl;
});

export const usePreferenceUrl = () => useStore((state) => {
  if (
    state.centerType === CenterTypes.PRIVACY_CENTER
      && state.privacyCenterActiveTabId === Ids.PREFERENCE_CENTER
  ) {
    return state.privacyCenterUrl;
  }
  return state.preferenceCenterUrl;
});
export const useConsentProfilesPurposes = () => useStore((state) => state.consentProfilesPurposes);
export const usePreferenceUpdateUrl = () => useStore((state) => state.preferenceUpdateUrl);

export const useResponse = () => useStore((state) => state.response);

export const useCenterType = () => useStore((state) => state.centerType);
export const useSelectedRequestTypeId = () => useStore((state) => state.selectedRequestTypeId);
export const useRequestFormData = () => useStore((state) => state.requestFormData);
export const useIsRequestFormValidationError = () => useStore((state) => state.isRequestFormValidationError);

export const useOtpTransactionId = () => useStore((state) => state.otpTransactionId);

export const updateStore = (payload) => {
  return useStore.setState(() => ({ ...payload }));
};

export const getRequestUrl = () => {
  const currentState = useStore.getState();
  if (currentState.centerType === CenterTypes.PRIVACY_CENTER && currentState.privacyCenterActiveTabId === Ids.REQUEST_CENTER) {
    return currentState.privacyCenterUrl;
  }
  return currentState.requestCenterUrl;
};

export const getPreferenceUrl = () => {
  const currentState = useStore.getState();
  if (currentState.centerType === CenterTypes.PRIVACY_CENTER && currentState.privacyCenterActiveTabId === Ids.PREFERENCE_CENTER) {
    return currentState.privacyCenterUrl;
  }
  return currentState.preferenceCenterUrl;
};
export const getConsentProfilesPurposes = () => useStore.getState().consentProfilesPurposes;
export const getPreferenceUpdateUrl = () => useStore.getState().preferenceUpdateUrl;
export const getOtpRequestUrl = () => useStore.getState().otpRequestUrl;
export const getOtpPayload = () => useStore.getState().otpPayload;
export const getOtpVerifyUrl = () => useStore.getState().otpVerifyUrl;
export const getOtpTransactionId = () => useStore.getState().otpTransactionId;
export const getOtpResendUrl = () => useStore.getState().otpResendUrl;
export const getOtpConfigUrl = () => useStore.getState().otpConfigUrl;

export const getVerifiedLocales = () => useStore.getState().verifiedLocales;
export const useRedirectUrl = () => useStore((state) => state.redirectUrl);
export const getIsWebSdk = () => useStore.getState().isWebSdk;
export const getIsAndroidSdk = () => useStore.getState().isAndroidSdk;
export const getIsIosSdk = () => useStore.getState().isIosSdk;
export const getIsMobileSdk = () => useStore.getState().isAndroidSdk || useStore.getState().isIosSdk;
export const getIsSdk = () => useStore.getState().isAndroidSdk || useStore.getState().isIosSdk || useStore.getState().isWebSdk;

window.useStore = useStore;
